import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Users, FileText, DollarSign, TrendingUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { StatCard } from "@/components/admin/dashboard/StatCard";
import { UserGrowthChart } from "@/components/admin/dashboard/UserGrowthChart";
import { RecentActivity } from "@/components/admin/dashboard/RecentActivity";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeSubs: 0,
    totalScripts: 0,
    monthlyRevenue: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchStats() {
      // Parallel requests for stats
      const [
        { count: userCount },
        { count: subCount },
        { count: scriptCount }
      ] = await Promise.all([
        supabase.from("profiles").select("*", { count: "exact", head: true }),
        supabase.from("subscriptions").select("*", { count: "exact", head: true }).eq("status", "active"),
        supabase.from("scripts").select("*", { count: "exact", head: true }),
      ]);

      // Mock revenue since we don't have a payments table yet in the prompt schema
      // In real world, query 'invoices' or similar
      const mockRevenue = (subCount || 0) * 15; // Assume $15 avg

      setStats({
        totalUsers: userCount || 0,
        activeSubs: subCount || 0,
        totalScripts: scriptCount || 0,
        monthlyRevenue: mockRevenue,
      });
      setLoading(false);
    }
    fetchStats();
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-8 py-8">
      {/* 1. Header Section */}
      <div className="mb-8 space-y-1">
        <h2 className="text-4xl font-black tracking-tighter text-[#1A1A1A]">{t('admin.dashboard.title')}</h2>
        <p className="text-lg font-medium text-gray-500">{t('admin.dashboard.subtitle')}</p>
      </div>

      {/* 2. Stats Grid */}
      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title={t('admin.dashboard.stats.totalUsers')}
          value={loading ? "-" : stats.totalUsers}
          icon={Users}
          color="yellow"
          trend={{ value: 12, label: t('admin.dashboard.stats.vsLastWeek') }}
          onClick={() => navigate('/admin/users')}
        />
        <StatCard
          title={t('admin.dashboard.stats.activeSubscriptions')}
          value={loading ? "-" : stats.activeSubs}
          icon={TrendingUp}
          color="cyan"
          trend={{ value: 5, label: t('admin.dashboard.stats.vsLastWeek') }}
          onClick={() => navigate('/admin/sales')}
        />
        <StatCard
          title={t('admin.dashboard.stats.scriptsCreated')}
          value={loading ? "-" : stats.totalScripts}
          icon={FileText}
          color="pink"
          onClick={() => navigate('/admin/content')}
        />
        <StatCard
          title={t('admin.dashboard.stats.estimatedRevenue')}
          value={loading ? "-" : new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(stats.monthlyRevenue)}
          icon={DollarSign}
          color="blue"
          trend={{ value: 8, label: t('admin.dashboard.stats.vsLastMonth') }}
          onClick={() => navigate('/admin/sales')}
        />
      </div>

      {/* 3. Charts & Activity */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main Chart - Takes up 2/3 on large screens */}
        <div className="lg:col-span-2">
          <UserGrowthChart />
        </div>

        {/* Recent Activity - Takes up 1/3 on large screens */}
        <div className="lg:col-span-1">
          <RecentActivity />
        </div>
      </div>
    </div>
  );
}
